import { Backdrop, Box, CircularProgress, useTheme } from '@mui/material';
import { some } from 'lodash';
import { ReactNode, useEffect } from 'react';
import analytics from 'src/analytics';
import TurnstileCaptcha from 'src/components/Cloudflare/TurnstileCaptcha';
import ImageComponent from 'src/components/common/Image';
import { PageErrorBoundary } from 'src/components/ErrorBoundary/PageErrorBoundary';
import PopupRedirect from 'src/features/Public/PopupRedirect';
import PoweredByBadge from 'src/features/Public/PoweredByBadge';
import useRefetch from 'src/hooks/useRefetch';
import { useRemoveDomElements } from 'src/hooks/useRemoveDomElements';
import { ME_KEY, useQueryTokenByApiKey } from 'src/services/queries';

import { ACCESS_TOKEN_KEY, AFFILIATE_CODE_WIDGET, API_KEY_WIDGET, REFRESH_TOKEN_KEY } from 'src/utils/constants/config';
import {
  AI_HS_CODE_LABELING_IMAGES,
  LOGO_INVOICE_URL,
  LOGO_WHITE_URL,
  TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES,
} from 'src/utils/constants/imagesUrl';
import { IFrameType, usePopupRedirectStore } from 'src/zustand/usePopupRedirectStore';

const PublicPageContainer = ({
  children,
  featureName,
  visiblePowerByBadge = true,
}: {
  children: ReactNode;
  featureName?: string;
  visiblePowerByBadge?: boolean;
}) => {
  const {
    palette: { background },
    zIndex,
  } = useTheme();

  useRemoveDomElements({
    ids: ['script-chatbox'],
    classNames: ['sb-chat-launcher-iframe', 'sb-chat-widget-iframe'],
  });

  const urlParams = new URLSearchParams(window.location.search);
  const apiKeyParam = urlParams.get('api_key');
  const accessTokenParam = urlParams.get('access_token');
  const affiliateCodeParam = urlParams.get('affiliate_code');
  const hostName = urlParams.get('host_name');
  const {
    data: { data },
    isFetching,
  } = useQueryTokenByApiKey({ api_key: apiKeyParam ?? '' });
  const { refetchQuery } = useRefetch();

  const setAccessTokenToLocal = () => {
    if (!data?.access_token) return;
    refetchQuery([ME_KEY]);
    localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.access_token);
  };

  useEffect(() => {
    setAccessTokenToLocal();
  }, [data]);

  const { visible, iframe, dispatch: dispatchPopup } = usePopupRedirectStore();

  useEffect(() => {
    console.log('Adamftd App Version:', process.env.REACT_APP_VERSION);
    if (!featureName) return;
    analytics.pushToDataLayer('iframe_access', {
      iframe_access_type: featureName,
      iframe_host_name: hostName || 'unknown',
    });
  }, [featureName, hostName]);

  useEffect(() => {
    preventOpenOnBrowser();
    const apiKeyWidget = localStorage.getItem(API_KEY_WIDGET);
    const affiliateCodeWidget = localStorage.getItem(AFFILIATE_CODE_WIDGET);

    if (apiKeyParam && apiKeyParam !== apiKeyWidget) {
      localStorage.setItem(API_KEY_WIDGET, apiKeyParam || '');
    }
    if (affiliateCodeParam && affiliateCodeParam !== affiliateCodeWidget) {
      localStorage.setItem(AFFILIATE_CODE_WIDGET, affiliateCodeParam || '');
    }
    if (accessTokenParam) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessTokenParam);
      localStorage.setItem(REFRESH_TOKEN_KEY, accessTokenParam);
      refetchQuery([ME_KEY]);
    }
  }, []);

  const preventOpenOnBrowser = () => {
    const isInIframe = window.self !== window.top;
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    if (isLocal) return;
    if (!isInIframe) {
      // The page is not in an iframe, block access
      document.body.innerHTML = ''; // Clear the content
      alert('Access denied. This page cannot be opened directly in the browser.');
    }
  };

  const getBannerPopup = (iframeType: IFrameType) => {
    switch (iframeType) {
      case IFrameType.AiHsCodeLabeling:
        return (
          <ImageComponent
            imgSrc={AI_HS_CODE_LABELING_IMAGES.banner_widget}
            style={{
              margin: '-32px -32px 0 -32px',
            }}
          />
        );
      case IFrameType.TariffAndRules:
        return <ImageComponent imgSrc={TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES.shipping_blue_box} />;
      default:
        return (
          <ImageComponent
            imgSrc={AI_HS_CODE_LABELING_IMAGES.banner_widget}
            style={{
              margin: '-32px -32px 0 -32px',
            }}
          />
        );
    }
  };

  const getLogoPopup = (iframeType: IFrameType) => {
    switch (iframeType) {
      case IFrameType.AiHsCodeLabeling:
        return LOGO_WHITE_URL;
      case IFrameType.TariffAndRules:
        return LOGO_INVOICE_URL;
      default:
        return LOGO_INVOICE_URL;
    }
  };

  return (
    <TurnstileCaptcha>
      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={some([isFetching])}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box minHeight={'100vh'} width={'100%'} bgcolor={background.paper1}>
        <PopupRedirect
          open={visible as boolean}
          handleClose={() => dispatchPopup({ visible: false })}
          title="Unlock Full Access to Tariffs, Trade Rules and Much More!"
          imageBanner={getBannerPopup(iframe || IFrameType.TariffAndRules)}
          imageLogo={
            <ImageComponent
              imgSrc={getLogoPopup(iframe || IFrameType.TariffAndRules)}
              style={{
                position: 'absolute',
              }}
              width={60}
            />
          }
        />
        {visiblePowerByBadge && <PoweredByBadge />}
        <PageErrorBoundary pageName="Public Page">{children}</PageErrorBoundary>
      </Box>
    </TurnstileCaptcha>
  );
};

export default PublicPageContainer;
